import React from "react";
import { Link } from "react-router-dom";
import CcgRoutePremiumPage from "../components/CcgRoutePremiumPage";
import PostcodeQuotenew from "./PostcodeQuotenew";
import "./CcgRoutePremium.css";

const cardGrid = (className, cards) => (
  <div className={className}>
    {cards.map((card) => (
      <article className={card.className || ""} key={card.title}>
        <h3>{card.title}</h3>
        {card.body ? <p>{card.body}</p> : null}
        {card.items ? <ul>{card.items.map((item) => <li key={item}>{item}</li>)}</ul> : null}
        {card.link ? <Link to={card.link.to} className={card.link.className || "route-link-inline"}>{card.link.label}</Link> : null}
        {card.links ? <div className="route-link-btn-row">{card.links.map((link) => <Link to={link.to} className={link.className || "route-link-btn"} key={link.to}>{link.label}</Link>)}</div> : null}
      </article>
    ))}
  </div>
);

const henleyToHeathrowRoute = {
  id: "henley-to-heathrow",
  path: "/henley-to-heathrow",
  liveStripVariant: "maidenhead",
  seo: {
    title: "Henley to Heathrow Taxi - Fixed-Price Airport Transfers | CheapCabGrab",
    description:
      "Book a fixed-price taxi from Henley-on-Thames or Henley Royal Regatta to Heathrow. Licensed drivers, live Heathrow checks, Regatta pickup planning and no surge pricing.",
    keywords:
      "Henley to Heathrow taxi, Henley Royal Regatta to Heathrow taxi, Henley-on-Thames airport transfer, Henley station to Heathrow taxi, CheapCabGrab",
    canonical: "https://www.cheapcabgrab.com/henley-to-heathrow",
    ogTitle: "Henley to Heathrow Taxi - Fixed Airport Transfers",
    ogDescription:
      "Reliable Henley to Heathrow taxis with fixed fares, licensed drivers, Regatta pickup planning and live Heathrow checks.",
    serviceName: "CheapCabGrab - Henley to Heathrow Taxi",
    areaServed: ["Henley-on-Thames", "Henley Royal Regatta", "Heathrow Airport", "Marlow", "Reading"],
    offerCatalogName: "Henley to Heathrow Taxi Routes",
    offerName: "Henley to Heathrow Airport fixed-price taxi",
    offerDescription: "Fixed-price taxi transfers from Henley-on-Thames, Regatta hotels and local addresses to Heathrow Airport terminals.",
    reservationName: "Taxi booking from Henley to Heathrow",
  },
  hero: {
    eyebrow: "Henley to Heathrow taxi route",
    h1: "Henley to Heathrow Taxi - Fixed-Price Airport Transfers",
    subtitle: "Henley-on-Thames, Regatta, town centre, hotel and station pickups for Heathrow terminals 2, 3, 4 and 5.",
    body:
      "Book reliable taxis from Henley to Heathrow with licensed drivers, live Heathrow checks and secure online booking. Fixed upfront fares with no surge pricing.",
  },
  quote: {
    label: "Get instant quotes",
    note: "Free - Fast - No obligation",
    presetPickup: "Henley-on-Thames",
    presetDropoff: "Heathrow Airport",
  },
  flags: [
    "Fixed fares agreed upfront - no meters",
    "Live Heathrow checks for roads, flights and terminals",
    "Henley town, station, hotels and Regatta pickups",
    "Saloon, MPV, minibus and coach options",
  ],
  reviewPageKey: "/henley-to-heathrow",
  reviewAriaLabel: "Henley to Heathrow customer reviews",
  lhrStatusTitle: "Live Heathrow checks for Henley to Heathrow taxis",
  floatingCta: "Book Henley to Heathrow Taxi",
  mobileCta: "Book Henley to Heathrow Taxi",
  supportNudge: "Need Henley to Heathrow help?",
  sections: [
    {
      id: "route-overview",
      title: "Henley to Heathrow at a glance",
      body: "A premium airport transfer route for Henley-on-Thames, Henley Royal Regatta guests, hotels, station pickups and Thames Valley visitors.",
      render: () => cardGrid("route-overview-grid", [
        {
          title: "Journey basics",
          items: [
            "Distance: Approx. 24-32 miles depending on pickup and terminal",
            "Typical time: 45-70 minutes",
            "Typical saloon fare: from 75-115 GBP",
            "Areas covered: Henley-on-Thames, RG9, Remenham, Hambleden, Shiplake and nearby hotels",
          ],
        },
        {
          title: "Great for",
          items: [
            "Henley Royal Regatta guests heading to Heathrow",
            "Henley station to Heathrow transfers",
            "Hotel guests with luggage, formalwear or hospitality items",
            "MPVs and minibuses for river event groups",
          ],
        },
        {
          title: "Local planning links",
          links: [
            { to: "/henley-taxi", label: "Henley taxi" },
            { to: "/maidenhead-to-heathrow", label: "Maidenhead to Heathrow", className: "route-link-btn route-link-btn-secondary" },
          ],
        },
      ]),
    },
    {
      id: "route-tools",
      title: "Useful tools for your Henley to Heathrow journey",
      body: "Check Heathrow Airport terminals, live status and local Henley transport options before you travel.",
      render: () => cardGrid("route-links-grid", [
        { title: "Heathrow Terminal Finder", body: "Check the correct Heathrow terminal before booking or travelling.", link: { to: "/terminalFinder", label: "Open Terminal Finder", className: "route-link-btn" } },
        { title: "Henley taxi hub", body: "For Regatta, town centre, station, hotel and event guidance, use the Henley taxi hub.", link: { to: "/henley-taxi", label: "Open Henley taxi", className: "route-link-btn" } },
        { title: "Group travel", body: "Need more luggage space from Henley? Use minibus or coach options for Regatta and airport groups.", links: [{ to: "/minibus", label: "Minibus" }, { to: "/coach", label: "Coach", className: "route-link-btn route-link-btn-secondary" }] },
      ]),
    },
    {
      id: "route-areas",
      title: "Henley to Heathrow pickup areas",
      body: "Book from Henley-on-Thames, Henley station, Regatta town centre drop-off points, Remenham, Hambleden, Shiplake and nearby RG9 addresses to Heathrow Airport.",
      render: () => cardGrid("route-areas-grid", [
        {
          title: "Henley and Regatta pickups",
          items: [
            "Henley-on-Thames town centre to Heathrow Airport",
            "Henley station to Heathrow Airport",
            "Regatta hotels, restaurants and hospitality pickups",
            "Remenham, Hambleden, Shiplake and nearby addresses",
          ],
          link: { to: "/henley-taxi", label: "Henley taxi hub ->" },
        },
        {
          title: "Nearby Heathrow routes",
          items: [
            "Maidenhead to Heathrow for M4 corridor hotel stays",
            "Windsor to Heathrow for Castle and visitor groups",
            "Taplow to Heathrow for Thames-side hotels",
          ],
          links: [
            { to: "/maidenhead-to-heathrow", label: "Maidenhead route" },
            { to: "/windsor-to-heathrow", label: "Windsor route", className: "route-link-btn route-link-btn-secondary" },
            { to: "/taplow-to-heathrow", label: "Taplow route", className: "route-link-btn route-link-btn-secondary" },
          ],
        },
      ]),
    },
    {
      id: "route-faq",
      title: "Henley to Heathrow taxi - FAQs",
      body: "Route FAQ for Henley-on-Thames and local Heathrow Airport transfers.",
      render: () => (
        <div className="route-faq-grid">
          {[
            ["How long does Henley to Heathrow take?", "Most Henley to Heathrow taxi journeys take around 45-70 minutes, depending on Regatta traffic, M4 or M40 routing, pickup point and terminal."],
            ["Can you pick up during Henley Royal Regatta?", "Yes. Enter the exact pickup point, hotel or town centre location. During Regatta week, allow extra time for one-way systems, walking routes and traffic controls."],
            ["Can you collect from Henley station?", "Yes. Henley-on-Thames station pickups are useful for passengers connecting from rail to Heathrow or local hotels."],
            ["Do you track Heathrow status?", "Yes. The page shows live Heathrow checks for roads, flights, terminal pressure, queues, rail and weather before you book."],
          ].map(([title, body]) => <article className="route-faq-card" key={title}><h3>{title}</h3><p>{body}</p></article>)}
        </div>
      ),
    },
    {
      id: "route-support",
      title: "Need help with a Henley to Heathrow booking?",
      body: "For Regatta timing, extra luggage, MPVs, child seats or multi-stop Heathrow Airport journeys, use support before booking.",
      render: () => (
        <div className="route-support-box">
          <p>Email: <a href="mailto:support@cheapcabgrab.com">support@cheapcabgrab.com</a></p>
          <p>
            Related pages: <Link to="/henley-taxi">Henley taxi</Link>,{" "}
            <Link to="/maidenhead-to-heathrow">Maidenhead to Heathrow</Link>,{" "}
            <Link to="/windsor-to-heathrow">Windsor to Heathrow</Link> and{" "}
            <Link to="/taplow-to-heathrow">Taplow to Heathrow</Link>.
          </p>
        </div>
      ),
    },
  ],
};

const HenleyToHeathrow = () => (
  <CcgRoutePremiumPage route={henleyToHeathrowRoute} QuoteComponent={PostcodeQuotenew} />
);

export default HenleyToHeathrow;
